import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Cookies } from "react-cookie-consent";

const LanguageContext = createContext(null);

/**
 * In-memory translation cache
 */
const translationCache = new Map();

const FALLBACK_LANGUAGE = "en";
const FALLBACK_TRANSLATION_URL =
  "/static/assets/Languages/en.json";

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      "useLanguage must be used within LanguageProvider"
    );
  }

  return context;
};

const getInitialLocale = () => {
  try {
    const savedLanguage = Cookies.get("locale");

    return {
      language: savedLanguage || FALLBACK_LANGUAGE,
    };
  } catch {
    return {
      language: FALLBACK_LANGUAGE,
    };
  }
};

export const LanguageProvider = ({
  children,
  bucketUrl,
  useLocalTranslations = false,
}) => {
  const [locale, setLocale] = useState(getInitialLocale);

  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(true);

  const { language } = locale;

  const getTranslationUrl = useCallback(
    (lang) => {
      const normalizedLang =
        lang?.toLowerCase() || FALLBACK_LANGUAGE;

      return useLocalTranslations
        ? `/static/assets/Languages/${normalizedLang}.json`
        : `${bucketUrl}assets/apac/${normalizedLang}/translations/translate.json`;
    },
    [bucketUrl, useLocalTranslations]
  );

  const loadTranslation = useCallback(async (url) => {
    const response = await fetch(url, {
      cache: "force-cache",
    });

    if (!response.ok) {
      throw new Error(
        `Failed to load translation from ${url}`
      );
    }

    return response.json();
  }, []);

  const fetchTranslations = useCallback(
    async (lang) => {
      const normalizedLang =
        lang?.toLowerCase() || FALLBACK_LANGUAGE;

      try {
        setLoading(true);

        /**
         * Return from memory cache
         */
        if (translationCache.has(normalizedLang)) {
          console.log(
            `Loaded ${normalizedLang} from memory cache`
          );

          setMessages(
            translationCache.get(normalizedLang)
          );

          return;
        }

        /**
         * Try requested language first
         */
        const data = await loadTranslation(
          getTranslationUrl(normalizedLang)
        );

        translationCache.set(
          normalizedLang,
          data
        );

        setMessages(data);

        console.log(
          `Loaded ${normalizedLang} from network`
        );
      } catch (error) {
        console.error(
          `Failed to load ${normalizedLang}. Falling back to English.`,
          error, bucketUrl
        );

        if (bucketUrl === undefined || bucketUrl === null || bucketUrl === "") {
          setMessages({});
          return;
        }
        try {
          /**
           * Return English from memory if already cached
           */
          if (translationCache.has(FALLBACK_LANGUAGE)) {
            console.log(
              "Loaded fallback English from memory cache"
            );

            setMessages(
              translationCache.get(FALLBACK_LANGUAGE)
            );

            return;
          }

          /**
           * Load English locally from static assets
           */
          const fallbackData = await loadTranslation(
            FALLBACK_TRANSLATION_URL
          );

          translationCache.set(
            FALLBACK_LANGUAGE,
            fallbackData
          );

          setMessages(fallbackData);

          console.log(
            "Loaded fallback English translation"
          );
        } catch (fallbackError) {
          console.error(
            "Failed to load fallback English translation",
            fallbackError
          );

          /**
           * Prevent application crash
           */
          setMessages({});
        }
      } finally {
        setLoading(false);
      }
    },
    [getTranslationUrl, loadTranslation]
  );

  /**
   * Load translations when language changes
   */
  useEffect(() => {
    document.documentElement.lang = language;

    fetchTranslations(language);
  }, [language, fetchTranslations]);

  /**
   * Preload English on startup
   * Improves fallback performance
   */
  useEffect(() => {
    const preloadEnglish = async () => {
      if (translationCache.has(FALLBACK_LANGUAGE)) {
        return;
      }

      try {
        const data = await loadTranslation(
          FALLBACK_TRANSLATION_URL
        );

        translationCache.set(
          FALLBACK_LANGUAGE,
          data
        );
      } catch (error) {
        console.error(
          "Failed to preload English translations",
          error
        );
      }
    };

    preloadEnglish();
  }, [loadTranslation]);

  const updateLocale = useCallback(
    ({ language: newLanguage }) => {
      setLocale((prev) => ({
        ...prev,
        ...(newLanguage && {
          language: newLanguage.toLowerCase(),
        }),
      }));
    },
    []
  );

  const setLanguage = useCallback(
    (lang) => {
      updateLocale({
        language: lang,
      });
    },
    [updateLocale]
  );

  const t = useCallback(
    (key, fallback = key) =>
      messages?.[key] ?? fallback,
    [messages]
  );

  const value = useMemo(
    () => ({
      language,
      loading,
      setLanguage,
      updateLocale,
      t,
    }),
    [language, loading, setLanguage, updateLocale, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};